import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../../contexts/AuthContext';
import { db } from '../../config/firebase';
import { collection, getDocs, addDoc, deleteDoc, doc, updateDoc, orderBy, query } from 'firebase/firestore';

export default function Dashboard() {
  const { currentUser, logout } = useAdminAuth();
  const navigate = useNavigate();
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  
  const [formData, setFormData] = useState({
    title: '',
    summary: '',
    content: '',
    category: 'Institutionnel',
    priority: 'high',
    image: ''
  });

  // Fonction de sanitization pour prévenir XSS
  function sanitizeInput(str) {
    if (!str) return '';
    return str
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#x27;')
      .replace(/\//g, '&#x2F;');
  }

  const categories = ['Institutionnel', 'Communiqué', 'Vie de l\'École', 'Formation', 'Événement'];
  const priorities = [
    { value: 'urgent', label: 'Urgent (en haut)', color: '#DC2626' },
    { value: 'high', label: 'Important', color: '#F59E0B' },
    { value: 'normal', label: 'Normal', color: '#6B7280' }
  ];

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  async function fetchAnnouncements() {
    try {
      const q = query(collection(db, 'announcements'), orderBy('published_at', 'desc'));
      const snapshot = await getDocs(q);
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setAnnouncements(data);
    } catch (error) {
      console.error('Erreur chargement:', error);
    }
    setLoading(false);
  }

  async function handleLogout() {
    await logout();
    navigate('/admin');
  }

  async function handleSubmit(e) {
    e.preventDefault();
    
    // Sanitize toutes les entrées texte
    const announcementData = {
      title: sanitizeInput(formData.title),
      summary: sanitizeInput(formData.summary),
      content: sanitizeInput(formData.content),
      category: formData.category,
      priority: formData.priority,
      image: formData.image.startsWith('/media/') ? formData.image : '', // Valider le chemin image
      published_at: editingId ? formData.published_at : new Date().toISOString().split('T')[0],
      views_count: editingId ? formData.views_count : 0,
      updatedAt: new Date().toISOString()
    };

    try {
      if (editingId) {
        await updateDoc(doc(db, 'announcements', editingId), announcementData);
      } else {
        await addDoc(collection(db, 'announcements'), announcementData);
      }
      
      setShowForm(false);
      setEditingId(null);
      setFormData({ title: '', summary: '', content: '', category: 'Institutionnel', priority: 'high', image: '' });
      fetchAnnouncements();
    } catch (error) {
      console.error('Erreur sauvegarde:', error);
      alert('Erreur lors de la sauvegarde');
    }
  }

  async function handleDelete(id) {
    if (!window.confirm('Supprimer cette actualité ?')) return;
    
    try {
      await deleteDoc(doc(db, 'announcements', id));
      fetchAnnouncements();
    } catch (error) {
      console.error('Erreur suppression:', error);
    }
  }

  function handleEdit(announcement) {
    setFormData(announcement);
    setEditingId(announcement.id);
    setShowForm(true);
  }

  const styles = {
    container: {
      minHeight: '100vh',
      background: '#F3F4F6'
    },
    header: {
      background: '#0B2C5F',
      color: '#fff',
      padding: '16px 32px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    main: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '32px'
    },
    card: {
      background: '#fff',
      borderRadius: '16px',
      padding: '24px',
      boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
      marginBottom: '24px'
    },
    btn: {
      padding: '12px 24px',
      borderRadius: '10px',
      border: 'none',
      fontWeight: 600,
      cursor: 'pointer',
      fontSize: '14px',
      transition: 'all 0.2s'
    },
    btnPrimary: {
      background: '#0B2C5F',
      color: '#fff'
    },
    btnSecondary: {
      background: '#E5E7EB',
      color: '#374151'
    },
    btnDanger: {
      background: '#FEE2E2',
      color: '#DC2626'
    },
    input: {
      width: '100%',
      padding: '12px 16px',
      border: '2px solid #E5E7EB',
      borderRadius: '10px',
      fontSize: '15px',
      outline: 'none',
      boxSizing: 'border-box',
      marginBottom: '16px'
    },
    label: {
      display: 'block',
      fontSize: '14px',
      fontWeight: 600,
      color: '#374151',
      marginBottom: '8px'
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse'
    },
    th: {
      textAlign: 'left',
      padding: '12px 16px',
      borderBottom: '2px solid #E5E7EB',
      fontSize: '13px',
      fontWeight: 700,
      color: '#6B7280',
      textTransform: 'uppercase'
    },
    td: {
      padding: '16px',
      borderBottom: '1px solid #E5E7EB',
      fontSize: '14px'
    }
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <header style={styles.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <img src="/logo_ema.png" alt="EMA" style={{ height: '40px' }} />
          <div>
            <h1 style={{ margin: 0, fontSize: '18px', fontWeight: 700 }}>Administration EMA</h1>
            <p style={{ margin: 0, fontSize: '12px', opacity: 0.8 }}>{currentUser?.email}</p>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <Link to="/" style={{ ...styles.btn, ...styles.btnSecondary, textDecoration: 'none' }}>
            Voir le site
          </Link>
          <button onClick={handleLogout} style={{ ...styles.btn, ...styles.btnSecondary }}>
            Déconnexion
          </button>
        </div>
      </header>

      {/* Main */}
      <main style={styles.main}>
        {/* Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px', marginBottom: '32px' }}>
          {[
            { label: 'Actualités publiées', value: announcements.length, icon: '📰', color: '#0B2C5F' },
            { label: 'Ce mois-ci', value: announcements.filter(a => new Date(a.published_at).getMonth() === new Date().getMonth()).length, icon: '📅', color: '#059669' },
            { label: 'Urgentes', value: announcements.filter(a => a.priority === 'urgent').length, icon: '🔴', color: '#DC2626' }
          ].map((stat, i) => (
            <div key={i} style={{ ...styles.card, display: 'flex', alignItems: 'center', gap: '16px' }}>
              <span style={{ fontSize: '32px' }}>{stat.icon}</span>
              <div>
                <div style={{ fontSize: '28px', fontWeight: 700, color: stat.color }}>{stat.value}</div>
                <div style={{ fontSize: '13px', color: '#6B7280' }}>{stat.label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Actions */}
        <div style={{ ...styles.card, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: '#0B2C5F' }}>
            Gestion des actualités
          </h2>
          <button 
            onClick={() => { setShowForm(true); setEditingId(null); setFormData({ title: '', summary: '', content: '', category: 'Institutionnel', priority: 'high', image: '' }); }}
            style={{ ...styles.btn, ...styles.btnPrimary }}
          >
            + Nouvelle actualité
          </button>
        </div>

        {/* Form Modal */}
        {showForm && (
          <div style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px'
          }}>
            <div style={{ ...styles.card, width: '100%', maxWidth: '600px', maxHeight: '90vh', overflow: 'auto' }}>
              <h3 style={{ margin: '0 0 24px', fontSize: '20px', fontWeight: 700, color: '#0B2C5F' }}>
                {editingId ? 'Modifier l\'actualité' : 'Nouvelle actualité'}
              </h3>
              
              <form onSubmit={handleSubmit}>
                <label style={styles.label}>Titre *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  style={styles.input}
                  placeholder="Titre de l'actualité"
                />

                <label style={styles.label}>Résumé *</label>
                <textarea
                  value={formData.summary}
                  onChange={(e) => setFormData({ ...formData, summary: e.target.value })}
                  required
                  style={{ ...styles.input, minHeight: '80px', resize: 'vertical' }}
                  placeholder="Court résumé affiché dans les listes"
                />

                <label style={styles.label}>Contenu complet</label>
                <textarea
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  style={{ ...styles.input, minHeight: '120px', resize: 'vertical' }}
                  placeholder="Contenu détaillé de l'actualité"
                />

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div>
                    <label style={styles.label}>Catégorie</label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      style={styles.input}
                    >
                      {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={styles.label}>Priorité</label>
                    <select
                      value={formData.priority}
                      onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                      style={styles.input}
                    >
                      {priorities.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                    </select>
                  </div>
                </div>

                <label style={styles.label}>Chemin de l'image</label>
                <input
                  type="text"
                  value={formData.image}
                  onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                  style={styles.input}
                  placeholder="/media/photos/nom-image.jpeg"
                />
                <p style={{ fontSize: '12px', color: '#6B7280', marginTop: '-12px', marginBottom: '16px' }}>
                  Placez d'abord l'image dans <code>public/media/photos/</code> puis entrez le chemin ici
                </p>
                {formData.image && (
                  <img src={formData.image} alt="Aperçu" style={{ width: '100%', maxHeight: '200px', objectFit: 'cover', borderRadius: '10px', marginBottom: '16px' }} />
                )}

                <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' }}>
                  <button type="button" onClick={() => setShowForm(false)} style={{ ...styles.btn, ...styles.btnSecondary }}>
                    Annuler
                  </button>
                  <button type="submit" style={{ ...styles.btn, ...styles.btnPrimary }}>
                    {editingId ? 'Enregistrer' : 'Publier'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Table */}
        <div style={styles.card}>
          {loading ? (
            <p style={{ textAlign: 'center', color: '#6B7280' }}>Chargement...</p>
          ) : announcements.length === 0 ? (
            <p style={{ textAlign: 'center', color: '#6B7280' }}>Aucune actualité publiée</p>
          ) : (
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Actualité</th>
                  <th style={styles.th}>Catégorie</th>
                  <th style={styles.th}>Date</th>
                  <th style={styles.th}>Priorité</th>
                  <th style={styles.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {announcements.map((item) => (
                  <tr key={item.id}>
                    <td style={styles.td}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        {item.image && (
                          <img src={item.image} alt="" style={{ width: '50px', height: '50px', objectFit: 'cover', borderRadius: '8px' }} />
                        )}
                        <div>
                          <div style={{ fontWeight: 600, color: '#1F2937' }}>{item.title}</div>
                          <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px' }}>{item.summary?.substring(0, 60)}...</div>
                        </div>
                      </div>
                    </td>
                    <td style={styles.td}>
                      <span style={{ background: '#E5E7EB', padding: '4px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: 600 }}>
                        {item.category}
                      </span>
                    </td>
                    <td style={styles.td}>{item.published_at}</td>
                    <td style={styles.td}>
                      <span style={{
                        background: item.priority === 'urgent' ? '#FEE2E2' : item.priority === 'high' ? '#FEF3C7' : '#E5E7EB',
                        color: item.priority === 'urgent' ? '#DC2626' : item.priority === 'high' ? '#D97706' : '#6B7280',
                        padding: '4px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: 600
                      }}>
                        {item.priority === 'urgent' ? 'Urgent' : item.priority === 'high' ? 'Important' : 'Normal'}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button onClick={() => handleEdit(item)} style={{ ...styles.btn, ...styles.btnSecondary, padding: '8px 12px' }}>
                          Modifier
                        </button>
                        <button onClick={() => handleDelete(item.id)} style={{ ...styles.btn, ...styles.btnDanger, padding: '8px 12px' }}>
                          Supprimer
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </main>
    </div>
  );
}
